// src/pages/DashboardPage.jsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSettings } from '../hooks/useSettings';
import { getVehicles, getAllDeadlines, isBolloEsente, daysDiff, scadStatus } from '../lib/dataService';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

export default function DashboardPage() {
  const { profile, isAdmin, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [vehicles,  setVehicles]  = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    if (!profile) return;
    (async () => {
      const vv = await getVehicles(profile);
      setVehicles(vv);
      const ids = vv.map(v => v.id);
      const dd  = await getAllDeadlines(ids);
      setDeadlines(dd);
      setLoading(false);
    })();
  }, [profile]);

  // Calcola alert: scadute + urgenti (entro 60gg)
  const alerts = [];
  if (!loading && settings) {
    vehicles.forEach(v => {
      deadlines
        .filter(d => d.vehicleId === v.id && d.attiva !== false)
        .forEach(d => {
          // Salta bollo se esente
          if (d.tipo === 'bollo' && isBolloEsente(v.annoImmatricolazione)) return;
          // Salta tipo disabilitato dall'admin
          if (settings.tipi?.[d.tipo]?.attivo === false) return;
          const dd2 = daysDiff(d.dataScadenza?.toDate?.() || d.dataScadenza);
          if (dd2 !== null && dd2 < 60) {
            alerts.push({ veicolo: v, scadenza: d, dd: dd2 });
          }
        });
    });
    alerts.sort((a, b) => a.dd - b.dd);
  }

  const scadute = alerts.filter(a => a.dd < 0);
  const urgenti = alerts.filter(a => a.dd >= 0);

  const greeting = () => {
    const h = new Date().getHours();
    const nome = profile?.displayName || profile?.nome || 'Benvenuto';
    if (h < 12) return `Buongiorno, ${nome} 👋`;
    if (h < 18) return `Buon pomeriggio, ${nome} 👋`;
    return `Buonasera, ${nome} 👋`;
  };

  const labelScad = (dd) => {
    if (dd < 0)  return `Scaduta da ${Math.abs(dd)} giorni`;
    if (dd === 0) return 'Scade oggi!';
    return `tra ${dd} giorni`;
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    const date = d?.toDate ? d.toDate() : new Date(d);
    return format(date, 'd MMMM yyyy', { locale: it });
  };

  const tipoLabel = {
    bollo: 'Bollo',
    assicurazione: 'Assicurazione',
    collaudo: 'Collaudo / Revisione',
    messa_in_moto: 'Messa in moto',
    certificazione: 'Certificazione',
  };

  if (loading) return <div style={styles.loading}>Carico la tua <img src="/garagememo-logo.svg" alt="GarageMemo" style={{height: '60px'}} />…</div>;

  return (
    <div style={styles.page}>
      {/* TOP BAR */}
      <div style={styles.topbar}>
        <span style={styles.logo}>🏎 <img src="/garagememo-logo.svg" alt="GarageMemo" style={{height: '60px'}} /></span>
        <div style={styles.topRight}>
          {isAdmin && <span style={styles.badgeAdmin}>Admin</span>}
          <button style={styles.navBtn} onClick={() => navigate('/garage')}>Garage</button>
          <button style={styles.navBtn} onClick={() => navigate('/admin')} hidden={!isAdmin}>Pannello</button>
          <button style={styles.logoutBtn} onClick={logout}>Esci</button>
        </div>
      </div>

      <div style={styles.content}>
        {/* GREETING */}
        <h2 style={styles.greeting}>{greeting()}</h2>
        <p style={styles.greetingSub}>
          {alerts.length > 0
            ? `Hai ${alerts.length} situazion${alerts.length === 1 ? 'e' : 'i'} da controllare`
            : 'Tutto in regola — nessuna urgenza'}
        </p>

        {/* SUMMARY BAR */}
        <div style={styles.summaryBar}>
          <SumCard
            val={scadute.length}
            label="Scadute / scoperte"
            type={scadute.length > 0 ? 'danger' : 'ok'}
          />
          <SumCard
            val={urgenti.length}
            label="In scadenza (60 gg)"
            type={urgenti.length > 0 ? 'warn' : 'ok'}
          />
          <SumCard val={vehicles.length} label="Veicoli totali" type="neutral" />
        </div>

        {/* TUTTO OK */}
        {alerts.length === 0 && (
          <div style={styles.okBanner}>
            <span style={{ fontSize: 28 }}>✅</span>
            <div>
              <div style={styles.okTitle}>Tutto in regola!</div>
              <div style={styles.okSub}>Nessuna scadenza urgente nei prossimi 60 giorni</div>
            </div>
          </div>
        )}

        {/* SCADUTE */}
        {scadute.length > 0 && (
          <>
            <div style={styles.secHead}>🔴 Scadute o scoperte</div>
            {scadute.map((a, i) => (
              <AlertRow
                key={i} alert={a}
                tipoLabel={tipoLabel}
                labelScad={labelScad}
                fmtDate={fmtDate}
                type="danger"
                onClick={() => navigate(`/garage/${a.veicolo.id}`)}
              />
            ))}
          </>
        )}

        {/* URGENTI */}
        {urgenti.length > 0 && (
          <>
            <div style={styles.secHead}>🟠 In scadenza entro 60 giorni</div>
            {urgenti.map((a, i) => (
              <AlertRow
                key={i} alert={a}
                tipoLabel={tipoLabel}
                labelScad={labelScad}
                fmtDate={fmtDate}
                type="warn"
                onClick={() => navigate(`/garage/${a.veicolo.id}`)}
              />
            ))}
          </>
        )}

        <button style={styles.garageBtn} onClick={() => navigate('/garage')}>
          → Entra nel garage
        </button>
        <button style={{ ...styles.garageBtn, marginTop: 8 }} onClick={() => navigate('/associazioni')}>
          🏅 Associazioni e tessere
        </button>
      </div>
    </div>
  );
}

function SumCard({ val, label, type }) {
  const colors = {
    danger:  { bg: '#fff0f0', border: '#ffcdd2', val: '#c62828' },
    warn:    { bg: '#fffbf0', border: '#ffe082', val: '#e65100' },
    ok:      { bg: '#f0fff4', border: '#c8e6c9', val: '#2e7d32' },
    neutral: { bg: '#f5f5f5', border: '#e0e0e0', val: '#1a1a1a' },
  };
  const c = colors[type];
  return (
    <div style={{ ...styles.sumCard, background: c.bg, borderColor: c.border }}>
      <div style={{ ...styles.sumVal, color: c.val }}>{val}</div>
      <div style={styles.sumLabel}>{label}</div>
    </div>
  );
}

function AlertRow({ alert: a, tipoLabel, labelScad, fmtDate, type, onClick }) {
  const colors = {
    danger: { bg: '#fff0f0', border: '#ffcdd2', pill: '#c62828', pillBg: '#ffebee' },
    warn:   { bg: '#fffbf0', border: '#ffe082', pill: '#e65100', pillBg: '#fff3e0' },
  };
  const c = colors[type];
  return (
    <div
      style={{ ...styles.alertRow, background: c.bg, borderColor: c.border, cursor: 'pointer' }}
      onClick={onClick}
    >
      <div>
        <div style={styles.alertVeh}>{a.veicolo.marca} {a.veicolo.modello}</div>
        <div style={{ ...styles.alertTipo, color: c.pill }}>
          {tipoLabel[a.scadenza.tipo] || a.scadenza.tipo}
        </div>
        <div style={styles.alertSub}>
          {a.veicolo.targa || 'targa n.d.'} · {a.veicolo.proprietari?.join(', ')} · {fmtDate(a.scadenza.dataScadenza)}
        </div>
      </div>
      <span style={{ ...styles.pill, color: c.pill, background: c.pillBg }}>
        {labelScad(a.dd)}
      </span>
    </div>
  );
}

const styles = {
  page:    { minHeight: '100vh', background: '#f0f2f5', fontFamily: "'Georgia', serif" },
  loading: { padding: 40, textAlign: 'center', fontSize: 18, color: '#888' },
  topbar:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: '#fff', borderBottom: '1px solid #e8e8e8', flexWrap: 'wrap', gap: 8 },
  logo:    { fontSize: 18, fontWeight: 600 },
  topRight:{ display: 'flex', alignItems: 'center', gap: 8 },
  badgeAdmin: { fontSize: 13, padding: '4px 12px', borderRadius: 20, background: '#ffebee', color: '#c62828', fontWeight: 600 },
  navBtn:  { background: 'none', border: '1px solid #e0e0e0', padding: '6px 14px', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', color: '#444' },
  logoutBtn: { background: 'none', border: 'none', fontSize: 14, cursor: 'pointer', color: '#999', fontFamily: 'inherit' },
  content: { padding: 20, maxWidth: 700, margin: '0 auto' },
  greeting:{ fontSize: 24, fontWeight: 600, margin: '20px 0 4px', color: '#1a1a1a' },
  greetingSub: { fontSize: 15, color: '#888', marginBottom: 20 },
  summaryBar: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 24 },
  sumCard: { borderRadius: 12, padding: '14px 16px', border: '1px solid' },
  sumVal:  { fontSize: 30, fontWeight: 600 },
  sumLabel:{ fontSize: 13, color: '#666', marginTop: 3 },
  okBanner:{ display: 'flex', alignItems: 'center', gap: 14, padding: '20px', borderRadius: 14, background: '#f0fff4', border: '1px solid #c8e6c9', marginBottom: 16 },
  okTitle: { fontSize: 17, fontWeight: 600, color: '#2e7d32' },
  okSub:   { fontSize: 14, color: '#4caf50', marginTop: 3 },
  secHead: { fontSize: 13, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '.7px', margin: '20px 0 10px' },
  alertRow:{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderRadius: 12, marginBottom: 8, border: '1px solid' },
  alertVeh:{ fontSize: 17, fontWeight: 600, color: '#1a1a1a' },
  alertTipo:{ fontSize: 14, marginTop: 3, fontWeight: 500 },
  alertSub:{ fontSize: 13, color: '#999', marginTop: 2 },
  pill:    { padding: '5px 14px', borderRadius: 20, fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 },
  garageBtn: { width: '100%', padding: 16, borderRadius: 12, border: '1.5px solid #ddd', background: '#fff', fontSize: 16, fontWeight: 500, cursor: 'pointer', marginTop: 20, fontFamily: 'inherit', color: '#1a1a1a' },
};
